import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { ConfigService } from './config.service';
import { JwtService } from '../../auth/services/jwt.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenShareService {
  private hubConnection: signalR.HubConnection | null = null;
  private connectPromise: Promise<void> | null = null;
  private activeRequesterEmail: string | null = null;
  private mediaStream: MediaStream | null = null;
  private frameSocket: WebSocket | null = null;
  private frameLoopHandle: number | null = null;
  private captureCanvas: HTMLCanvasElement | null = null;
  private captureCtx: CanvasRenderingContext2D | null = null;

  readonly screenShareRequest$ = new Subject<string | null>();
  private enabled = false;

  constructor(
    private readonly configService: ConfigService,
    private readonly jwtService: JwtService,
    private readonly toastService: ToastService,
  ) {}

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  connect(): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    const certHubUrl = this.configService.config.certHubUrl;
    if (!certHubUrl) return Promise.resolve();

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${certHubUrl}/hubs/screenshare`, {
        accessTokenFactory: () => this.jwtService.getToken() ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connectPromise = this.hubConnection
      .start()
      .then(() => this.hubConnection!.invoke('RegisterEdportal'))
      .catch((err) => {
        console.error('SignalR connection error: ', err);
        this.hubConnection = null;
        this.connectPromise = null;
      });

    this.registerHubEventHandlers();

    return this.connectPromise;
  }

  registerHubEventHandlers(): void {
    // When an evaluator sends a request to the educator to share their screen, then we want to show the banner.
    this.hubConnection?.on('ReceiveScreenShareRequest', (requesterEmail: string) => {
      this.screenShareRequest$.next(requesterEmail);
    });

    this.hubConnection?.on('RegisteredAsSharer', () => {});

    this.hubConnection?.on('BeginSharing', async (requesterEmail: string) => {
      await this.startCapture(requesterEmail);
    });

    this.hubConnection?.on('ScreenShareStopped', () => {
      this.toastService.info(`Screen share session ended.`, true);
      this.stopCapture();
      this.screenShareRequest$.next(null);
    });
  }

  disconnect(): void {
    this.hubConnection?.stop();
    this.hubConnection = null;
    this.connectPromise = null;
  }

  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  async acceptScreenShare(requesterUserEmail: string): Promise<void> {
    await this.hubConnection?.invoke('AcceptScreenShare', requesterUserEmail);
    this.screenShareRequest$.next(null); // Hides the banner since the request has been accepted
  }

  async declineScreenShare(requesterUserEmail: string): Promise<void> {
    await this.hubConnection?.invoke('DeclineScreenShare', requesterUserEmail);
    this.screenShareRequest$.next(null); // Hides the banner since the request has been declined
  }

  async stopScreenShare(requesterUserEmail: string): Promise<void> {
    this.toastService.info(`Screen share session ended.`, true);
    this.stopCapture(); // Stop locally first regardless of hub outcome
    this.screenShareRequest$.next(null);
    await this.hubConnection?.invoke('StopScreenShare', requesterUserEmail);
  }

  async startCapture(requesterEmail: string): Promise<void> {
    this.activeRequesterEmail = requesterEmail;
    try {
      this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser',
          frameRate: 3,
          width: { max: 1280 }, // Limit resolution to reduce bandwidth
          height: { max: 720 },
        },
        audio: false,
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean });
    } catch (err) {
      this.activeRequesterEmail = null;
      if (requesterEmail) await this.hubConnection?.invoke('StopScreenShare', requesterEmail);
      return;
    }

    // Show a preview of what we are sharing (optional, useful for debugging)
    const preview = document.getElementById('preview') as HTMLVideoElement | null;
    if (preview) {
      preview.srcObject = this.mediaStream;
      preview.style.display = 'block';
    }

    // If the user stops sharing via the browser's built-in "Stop sharing" button,
    // the track ends and we need to clean up.
    this.mediaStream.getVideoTracks()[0].addEventListener('ended', () => {
      this.toastService.info(`Screen share session ended.`, true);
      const requester = this.activeRequesterEmail;
      this.stopCapture();
      if (requester) this.hubConnection?.invoke('StopScreenShare', requester);
    });

    const certHubUrl = this.configService.config.certHubUrl;
    if (!certHubUrl) {
      return;
    }
    const certWsUrl = certHubUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    this.frameSocket = new WebSocket(
      `${certWsUrl}/ws/frames?access_token=${encodeURIComponent(this.jwtService.getToken() ?? '')}&role=sender`,
    );
    this.frameSocket.binaryType = 'arraybuffer';

    this.frameSocket.addEventListener('open', () => {
      // Tell the server who to forward our frames to
      this.frameSocket!.send(`pair:${requesterEmail}`);

      // Start the capture loop after the socket is ready
      this.beginFrameLoop();
    });

    this.frameSocket.addEventListener('close', (ev) => {
      this.stopCapture();
    });

    this.frameSocket.addEventListener('error', (err) => {});
  }

  stopCapture(): void {
    this.activeRequesterEmail = null;
    if (this.frameLoopHandle !== null) {
      window.clearTimeout(this.frameLoopHandle);
      this.frameLoopHandle = null;
    }
    if (this.frameSocket) {
      const socket = this.frameSocket;
      this.frameSocket = null; // null before close to prevent re-entrant call from 'close' event
      socket.close();
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
    const preview = document.getElementById('preview') as HTMLVideoElement | null;
    if (preview) {
      preview.srcObject = null;
      preview.style.display = 'none';
    }
  }

  beginFrameLoop(): void {
    const preview = document.getElementById('preview') as HTMLVideoElement;
    if (!preview || !this.frameSocket) return;

    if (!this.captureCanvas) {
      this.captureCanvas = document.createElement('canvas');
      this.captureCanvas.width = 1280;
      this.captureCanvas.height = 720;
      this.captureCtx = this.captureCanvas.getContext('2d');
    }

    const sendFrame = () => {
      if (!this.frameSocket || this.frameSocket.readyState !== WebSocket.OPEN) return;
      this.captureCtx!.drawImage(preview, 0, 0, this.captureCanvas!.width, this.captureCanvas!.height);
      this.captureCanvas!.toBlob(
        (blob) => {
          if (blob && this.frameSocket?.readyState === WebSocket.OPEN) {
            this.frameSocket.send(blob);
          }
        },
        'image/jpeg',
        0.7,
      );
      this.frameLoopHandle = window.setTimeout(sendFrame, 333); // ~3fps
    };

    this.frameLoopHandle = window.setTimeout(sendFrame, 0);
  }
}
